import { initContract } from '@ts-rest/core'
import { ErrorResponseSchema } from '../../shared/types'
import {
  AttributePromotionsPostRequest,
  AttributePromotionsPostResponse,
  AttributionPostRequest,
  AttributionPostResponse,
  BulkPromotionPostRequest,
  BulkPromotionPostResponse,
  BulkPromotionUserPostRequest,
  BulkPromotionUserPostResponse,
  BulkRafCampaignPostRequest,
  BulkRafCampaignPostResponse,
  GenericCrudDeleteRequest,
  GenericCrudDeleteResponse,
  GenericCrudPathParams,
  GenericCrudPostResponse,
  GetRafCampaignsResponse,
  GetUserReferralsQuery,
  GetUserReferralsResponse,
  MigrateMultiUsePromotionUserRequest,
  MigratePromotionsRequest,
  PromotionByCodeGetResponse,
  PromotionByCodePathParams,
  PromotionByIdPathParams,
  PromotionCategoryGetResponse,
  PromotionGetRequest,
  PromotionGetResponse,
  PromotionPatchRequest,
  PromotionPostRequest,
  PromotionSchema,
  PromotionUserGetResponse,
  PromotionUserPathParams,
  PromotionUserPostRequest,
  PromotionUserPostResponse,
  PromotionUsersGetRequest,
  PromotionUsersGetResponse,
  PromotionUsersRevertResponse,
  PromotionValidateRequest,
  PromotionValidateResponse,
  RafCampaignIdPathParams,
  RafCouponGetQuery,
  ReferrerPromotionPostRequest,
  RepointRafCouponsToCampaignPostRequest,
  RepointRafCouponsToCampaignPostResponse,
  UserIdPathParams,
} from './schemas'

const c = initContract()

export const promotionsContract = c.router({
  getPromotionCategories: {
    method: 'GET',
    path: '/categories',
    summary: 'Retrieve all promotion categories',
    responses: {
      200: PromotionCategoryGetResponse,
      500: ErrorResponseSchema,
    },
  },

  getPromotions: {
    method: 'GET',
    path: '/',
    summary: 'Retrieve all promotions (discounts, referrals)',
    query: PromotionGetRequest,
    responses: {
      200: PromotionGetResponse,
      400: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  getRafCampaigns: {
    method: 'GET',
    path: '/raf-campaigns',
    summary: 'List all RAF campaigns ordered by end_date ASC',
    responses: {
      200: GetRafCampaignsResponse,
      500: ErrorResponseSchema,
    },
  },

  bulkCreateRafCampaigns: {
    method: 'POST',
    path: '/raf-campaigns/bulk',
    summary: 'Bulk create RAF campaigns with find-or-create semantics on (brand, name)',
    body: BulkRafCampaignPostRequest,
    responses: {
      201: BulkRafCampaignPostResponse,
      400: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  repointRafCouponsToCampaign: {
    method: 'POST',
    path: '/raf-campaigns/:campaignId/repoint-coupons',
    summary: 'Point a brand\'s existing referral coupons at this campaign\'s referee discount',
    pathParams: RafCampaignIdPathParams,
    body: RepointRafCouponsToCampaignPostRequest,
    responses: {
      200: RepointRafCouponsToCampaignPostResponse,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  getUserReferrals: {
    method: 'GET',
    path: '/referrers/:userId/referrals',
    pathParams: UserIdPathParams,
    query: GetUserReferralsQuery,
    summary: 'Get RAF referrals and rewards for a referrer',
    responses: {
      200: GetUserReferralsResponse,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  createReferrerPromotion: {
    method: 'POST',
    path: '/raf',
    body: ReferrerPromotionPostRequest,
    summary: 'Create a referrer promotion for a user',
    responses: {
      201: GenericCrudPostResponse,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  getPromotionUsers: {
    method: 'GET',
    path: '/promotion-users',
    summary: 'Filter promotion user records',
    query: PromotionUsersGetRequest,
    responses: {
      200: PromotionUsersGetResponse,
      400: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  getPromotionUser: {
    method: 'GET',
    path: '/promotion-users/:id',
    summary: 'Retrieve a promotion_user record by ID',
    pathParams: PromotionUserPathParams,
    responses: {
      200: PromotionUserGetResponse,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  getPromotionById: {
    method: 'GET',
    path: '/:id',
    summary: 'Retrieve specific promotion via specifiying a UUID',
    pathParams: PromotionByIdPathParams,
    responses: {
      200: PromotionSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  getPromotionByCode: {
    method: 'GET',
    path: '/:brand/:code',
    summary: 'Retrieve specific promotion via a coupon value and brand',
    pathParams: PromotionByCodePathParams,
    responses: {
      200: PromotionByCodeGetResponse,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  createPromotion: {
    method: 'POST',
    path: '/',
    summary: 'Create a new promotion',
    body: PromotionPostRequest,
    responses: {
      201: GenericCrudPostResponse,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      409: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  updatePromotion: {
    method: 'PATCH',
    path: '/:uuid',
    summary: 'Update an existing promotion',
    description: 'Please be aware, setting a max uses to a lower value will trigger a check to ensure this promotion has not been already used at the previous max uses value',
    pathParams: GenericCrudPathParams,
    body: PromotionPatchRequest,
    responses: {
      200: PromotionSchema,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      409: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  deletePromotion: {
    method: 'DELETE',
    path: '/:uuid',
    pathParams: GenericCrudPathParams,
    summary: 'Soft-delete a promotion',
    body: GenericCrudDeleteRequest,
    responses: {
      200: GenericCrudDeleteResponse,
    },
  },

  hardDeletePromotion: {
    method: 'DELETE',
    path: '/:uuid/hard',
    pathParams: GenericCrudPathParams,
    summary: 'Hard-delete a promotion and all associated promotion_user records',
    body: null,
    responses: {
      200: GenericCrudDeleteResponse,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  attribute: {
    method: 'POST',
    path: '/attributions',
    summary: 'Attribute a promotion usage with a user. This could be for discount, or referral purposes',
    body: AttributionPostRequest,
    responses: {
      200: AttributionPostResponse,
      400: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  attributePromotions: {
    method: 'POST',
    path: '/attribute',
    summary: 'Attribute a user\'s promotion usages (discount and/or referral). Retrying returns the existing checkout records',
    body: AttributePromotionsPostRequest,
    responses: {
      200: AttributePromotionsPostResponse,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  validatePromotion: {
    method: 'POST',
    path: '/validate',
    summary: 'Validate a promotion for adding to cart (pre-checkout validation)',
    body: PromotionValidateRequest,
    responses: {
      200: PromotionValidateResponse,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  bulkCreatePromotions: {
    method: 'POST',
    path: '/bulk',
    summary: 'Bulk upsert promotions. Creates, updates, or skips records based on id presence and brand+value uniqueness.',
    body: BulkPromotionPostRequest,
    responses: {
      200: BulkPromotionPostResponse,
      400: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  createPromotionUser: {
    method: 'POST',
    path: '/promotion-users',
    summary: 'Upsert a promotion_user record linking a user to a promotion. Matches on id and updates if exists.',
    body: PromotionUserPostRequest,
    responses: {
      201: PromotionUserPostResponse,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  bulkCreatePromotionUsers: {
    method: 'POST',
    path: '/promotion-users/bulk',
    summary: 'Bulk upsert promotion users. Creates, updates, or skips records based on id presence',
    body: BulkPromotionUserPostRequest,
    responses: {
      200: BulkPromotionUserPostResponse,
      400: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },

  hardDeletePromotionUser: {
    method: 'DELETE',
    path: '/promotion-users/:id/hard',
    pathParams: PromotionUserPathParams,
    summary: 'Hard-delete a promotion_user record',
    body: null,
    responses: {
      200: GenericCrudDeleteResponse,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },
  migratePromotions: {
    method: 'POST',
    path: '/migrate',
    summary: 'Migrate multi-use coupons from Laravel to Phoenix as promotions',
    body: MigratePromotionsRequest,
    responses: {
      200: GenericCrudPostResponse,
      400: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },
  migrateMultiUsePromotionUser: {
    method: 'POST',
    path: '/migrate/multi-use-promotion-users',
    summary: 'Migrate a multi_use_coupons_users row from Laravel to Phoenix as a promotion_user',
    body: MigrateMultiUsePromotionUserRequest,
    responses: {
      200: GenericCrudPostResponse,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },
  revertMigratedPromotionUsers: {
    method: 'DELETE',
    path: '/migration/revert/promotion-users/:userId',
    pathParams: UserIdPathParams,
    summary: 'Revert promotion user records created during the bulk migration',
    body: null,
    responses: {
      200: PromotionUsersRevertResponse,
      500: ErrorResponseSchema,
    },
  },
  getLatestRafCouponForUser: {
    method: 'GET',
    path: '/raf-coupons/users/:userId',
    pathParams: UserIdPathParams,
    query: RafCouponGetQuery,
    summary: 'Retrieve the latest active referral-coupon promotion owned by the user for the given brand (expireAt is NULL or NOT outdated, disabledAt is NULL, ordered by activeAt DESC)',
    responses: {
      200: PromotionSchema,
      400: ErrorResponseSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },
}, {
  pathPrefix: '/promotions',
})
